<template>
  <div v-if="currentPayrollRequest" class="review-payroll-request" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header" v-translate>Review details and confirm</h1>
    </div>

    <div class="form-container donation-summary">
      <!-- Donation summary -->
      <div class="summary-container">
        <!-- Payment type -->
        <div class="summary-item">
          <div class="field-name" v-translate>Payment method</div>
          <div class="summary-item-value" v-translate>Payroll</div>
        </div>

        <!-- Payment frequency -->
        <div class="summary-item">
          <div class="field-name" v-translate>Frequency</div>
          <div class="field-value">{{ formattedFrequency }}</div>
        </div>

        <!-- Payment amount -->
        <div class="summary-item">
          <div class="field-name" v-translate>Amount</div>
          <div class="field-value">{{ currentPayrollRequest.amount | currency }}</div>
        </div>
      </div>

      <!-- Donor fields -->
      <!-- First name -->
      <div class="form-field">
        <div class="field-name" v-translate>First name</div>
        <p class="summary-item-value">{{ currentPayrollRequest.firstName }}</p>
      </div>

      <!-- Last name -->
      <div class="form-field">
        <div class="field-name" v-translate>Last name</div>
        <p class="summary-item-value">{{ currentPayrollRequest.lastName }}</p>
      </div>

      <!-- Email -->
      <div class="form-field">
        <div class="field-name" v-translate>Email</div>
        <p class="summary-item-value">{{ currentPayrollRequest.email }}</p>
      </div>

      <!-- Unique ID -->
      <div v-if="organizationDetails.verificationMethod == 'EMPLOYEE_CODE'" class="form-field">
        <div class="field-name" v-translate>Unique ID</div>
        <p class="summary-item-value">{{ currentPayrollRequest.employeeId }}</p>
      </div>

      <!-- Zip code -->
      <div class="form-field" v-if="!userIsLoggedIn">
        <div class="field-name" v-translate>Zip code</div>
        <p class="summary-item-value">{{ currentPayrollRequest.zipCode }}</p>
      </div>

      <!-- Edit donation -->
      <div class="form-field edit-dontation">
        <a @click="onClickBack" v-translate>Edit donation information</a>
      </div>
    </div>

    <div class="form-container donation-summary-terms">
      <!-- T&C -->
      <div class="form-field card-shape">
        <div class="form-field-container">
          <div class="checkboxes">
            <label class="checkbox">
              <input type="checkbox" id="termsAndConditions" v-model="agreedToTerms" />
              <span class="form-checkbox" />
              <span class="tandc">
                {{ declaration }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Disclaimer -->
      <div v-if="organizationDetails.verificationMethod == 'EMPLOYEE_CODE'" class="form-header">
        <span v-translate>
          Clicking Confirm will send this payroll deduction request to your payroll department. They
          will process your request and reach out to you directly with any additional questions.
          Should you wish to change your payroll deductions, contact your payroll department
          directly.
        </span>
      </div>

      <!-- Submit -->
      <div class="button-container">
        <LoadingSpinnerButton
          class="form-button"
          :text="confirm"
          :loading="loading"
          :disabled="!agreedToTerms"
          @click="onConfirm"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import { analyticsMixin } from '@/services/googleAnalytics'
import PayrollRequest from '@/services/payrollRequests'
import AnonymousDonor from '@/services/anonymousDonors'

import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import CheckboxWithTerms from '@/components/CheckboxWithTerms'

export default {
  name: 'PayrollReview',
  mixins: [analyticsMixin],
  components: {
    LoadingSpinnerButton,
    CheckboxWithTerms,
  },
  data() {
    return {
      loading: false,
      agreedToTerms: false,
      anonymousDonor: AnonymousDonor.create({
        orgRelationship: AnonymousDonor.options.EMPLOYEE,
        organization: this.$store.state.organizationDetails.id,
      }),
    }
  },
  created() {
    // If this page is inappropriately accessed without a payroll request, route Home.
    if (!this.currentPayrollRequest) {
      this.$router.push({ name: 'Home' })
    }
  },
  methods: {
    onClickBack() {
      this.$router.push({ name: 'AddPayment', params: { donationType: 'payroll' } })
    },
    /* Submit payroll request! */
    async onConfirm() {
      this.loading = true
      try {
        if (this.userIsLoggedIn) {
          await this.submitRequest()
        } else {
          await this.submitRequestAnonymous()
        }
      } finally {
        this.loading = false
      }
    },
    async submitRequest() {
      return PayrollRequest.api.create(this.currentPayrollRequest).then(result => {
        // Clear out relevant info from state, since it's been processed
        this.$store.dispatch('clearCurrentPayrollRequest')
        // Route to summary screen
        this.$router.push({ name: 'PayrollSummary', params: { id: result.id } })
      })
    },
    async submitRequestAnonymous() {
      // First, populate an anonymous donor with our payroll request info
      this.anonymousDonor.firstName = this.currentPayrollRequest.firstName
      this.anonymousDonor.lastName = this.currentPayrollRequest.lastName
      this.anonymousDonor.email = this.currentPayrollRequest.email
      this.anonymousDonor.employeeId = this.currentPayrollRequest.employeeId
      this.anonymousDonor.zipCode = this.currentPayrollRequest.zipCode

      // Create the donor in the database
      const donor = await AnonymousDonor.api.create(this.anonymousDonor)
      const params = { donorId: donor.id, magicToken: donor.magicToken }

      // Using the credentials we received, submit payroll request
      return PayrollRequest.api.create(this.currentPayrollRequest, params).then(result => {
        // Clear out relevant info from state, since it's been processed
        this.$store.dispatch('clearCurrentPayrollRequest')
        // Route to summary screen
        this.$router.push({
          name: 'PayrollSummary',
          params: { id: result.id },
          query: params,
        })
      })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentPayrollRequest']),
    ...mapGetters(['userIsLoggedIn']),
    formattedFrequency() {
      return PayrollRequest.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.currentPayrollRequest.frequency,
      ).label
    },
    declaration() {
      const msg = this.$gettext('I hereby request that the amount specified above be taken out of my paycheck using after tax dollars, at the frequency specified above, and donated to the %{orgName} %{programName}.')
      const placeholders = {
        orgName: this.organizationDetails.name,
        programName: this.organizationDetails.programName
      }

      return this.$gettextInterpolate(msg, placeholders)
    },
    confirm() {
      return this.$gettext('Confirm')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.review-payroll-request {
  display: flex;
  flex-direction: column;
}

.donation-summary,
.donation-summary-terms {
  @extend .constrain-tablet-and-up;
}

.donation-summary {
  @include mobile-screen {
    margin-left: 5rem;
    margin-right: 5rem;
  }
}

.donation-summary-terms {
  margin-top: 0;
}

.summary-container {
  display: flex;
  align-items: center;
  margin-bottom: 2.5rem;

  @include mobile-screen {
    flex-direction: column;
  }

  @include tablet-and-up-screen {
    flex-direction: row;
  }
}

.summary-item {
  background-color: var(--light-brand-color);
  border-radius: 12px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;

  @include mobile-screen {
    width: 100%;
  }

  @include tablet-and-up-screen {
    width: 33%;
    margin-left: 1rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:nth-child(1) {
      margin-left: 0;
    }

    &:nth-child(3) {
      margin-right: 0;
    }
  }
}

.form-field {
  margin-bottom: 1.5rem;
}

.edit-dontation {
  margin-bottom: 0;

  a {
    font-weight: bold;
  }
}

.checkbox span.form-checkbox + span {
  font-family: 'Helvetica Neue';
  font-size: 18px;
  line-height: 28px;
}
</style>
